import React, { useEffect, useState } from 'react';
import { Box, Grid, Typography } from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import { useTranslation } from 'react-i18next';
import moment from 'moment';
import { CellProps, useTable } from '../table/useTable';
import {
  AccountData,
  ActionSource,
  AuditLogService,
  AuditType,
  OrderService,
  PropelUserModel,
  UserService,
} from '../api';
import {
  AuditLogData,
  AuditLogFilters,
  isEventAuditLog,
  isOrderAuditLog,
  isOrderPageAuditLog,
  isPassAuditLog,
  isUserAuditLog,
} from './types';
import { AuditLogChangeDetails } from './components/AuditLogChangeDetails';
import { AuditLogFlowVisualization } from './components/AuditLogFlowVisualization';
import { AuditLogEntityLink } from './components/AuditLogEntityLink';
import { AuditTypeFilter } from './components/AuditTypeFilter';
import { RefundAuditDetails } from './components/RefundAuditDetails';
import { getAuditTypeIcon } from './utils';
import { useAuth } from '../session/InternalAuthProvider';
import { getDateFormat, getTimeFormat } from '../common/getDateAndTimeFormat';
import RotatingLoader from '../common/ui/RotatingLoader';
import EditOrderDialog from '../calendar/dialogs/EditOrderDialog/EditOrderDialog';
import { useCalendar } from '../calendar/CalendarContext/CalendarContext';
import { mb } from '../common/theme/mbTokens';
import { PunchAdjustmentAuditDetails } from './components/PunchAdjustmentAuditDetails';

export const AuditLogPage: React.FC = () => {
  const { authState } = useAuth();
  const account = authState.account as unknown as AccountData;
  const { t } = useTranslation();
  const [filters, setFilters] = useState<AuditLogFilters>({});
  const [isLoadingUsers, setIsLoadingUsers] = useState(false);
  const { openEditOrderDrawer, editOrderDrawerState, closeEditOrderDrawer } = useCalendar();
  const [isOpeningOrder, setIsOpeningOrder] = useState(false);

  // Audit logs only carry order_id, but the edit drawer needs the whole order.
  const openOrder = async (orderId: string) => {
    if (isOpeningOrder) return;
    setIsOpeningOrder(true);
    try {
      const order = await OrderService.getOrder(orderId);
      openEditOrderDrawer({ order });
    } catch (error) {
      console.error('Error loading order for audit log entry:', error);
    } finally {
      setIsOpeningOrder(false);
    }
  };
  const [users, setUsers] = useState<PropelUserModel[]>([]);
  useEffect(() => {
    const fetchUsers = async () => {
      const users = await UserService.getUsers();
      if (setUsers) {
        setUsers(users);
      }
      setIsLoadingUsers(false);
    };
    void fetchUsers();
  }, []);

  const fetcher = async ({ search, page, perPage }: { search?: string; page: number; perPage: number }) => {
    let orderNumber: number | undefined;

    if (search) {
      const parsedOrderNumber = parseInt(search, 10);
      if (!Number.isNaN(parsedOrderNumber)) {
        orderNumber = parsedOrderNumber;
      }
    }

    try {
      const response = await AuditLogService.getAuditLogs(
        filters.auditTypes,
        undefined,
        undefined,
        orderNumber,
        filters.startDate,
        filters.endDate,
        page,
        perPage,
      );
      return response as AuditLogData[];
    } catch (error) {
      console.error('Error fetching audit logs:', error);
      throw error;
    }
  };

  const getAuditTypeLabel = (auditType: AuditType): string => {
    return t(`auditlog.audit_types.${auditType}`);
  };

  const tableDetails: CellProps<AuditLogData>[] = [
    {
      id: 'timestamp',
      alignment: 'left',
      label: t('auditlog.table.columns.time'),
      render: (log: AuditLogData) => (
        <Typography variant="body2">
          {moment(log.created_at).format(`${getDateFormat(account).longDateFormat}  ${getTimeFormat(account)}`)}
        </Typography>
      ),
    },
    {
      id: 'type',
      alignment: 'justify',
      label: t('auditlog.table.columns.action'),
      render: (log: AuditLogData) => (
        <Box display="flex" gap={1} justifyContent="flex-start">
          <span style={{ fontSize: '16px' }}>{getAuditTypeIcon(log.audit_type)}</span>
          <Typography variant="body2">{getAuditTypeLabel(log.audit_type)}</Typography>
        </Box>
      ),
    },
    {
      id: 'entity',
      alignment: 'left',
      label: t('auditlog.table.columns.entity'),
      render: (log: AuditLogData) => (
        <AuditLogEntityLink
          log={log}
          onOrderClick={(orderId) => {
            void openOrder(orderId);
          }}
        />
      ),
    },
    {
      id: 'user',
      alignment: 'left',
      label: t('auditlog.table.columns.user'),
      render: (log: AuditLogData) => {
        const user = users.find((user: PropelUserModel) => user.user_id === log.user_id);
        const resolvedName = user ? `${user.first_name} ${user.last_name}` : undefined;
        return (
          <Typography noWrap>
            {log.source === ActionSource.API
              ? `API${resolvedName ? ` (${resolvedName})` : ''}`
              : resolvedName ||
                (log.user_id === 'system'
                  ? t('auditlog.system')
                  : log.user_id
                  ? t('auditlog.mbuser')
                  : t('auditlog.customer'))}
          </Typography>
        );
      },
    },
    {
      id: 'changes',
      alignment: 'left',
      label: t('auditlog.table.columns.changes'),
      render: (log: AuditLogData) => {
        if (isOrderAuditLog(log) && log.changes.length > 0) {
          return <AuditLogChangeDetails changes={log.changes} users={users} />;
        }
        if (isEventAuditLog(log) && log.changes.length > 0) {
          return <AuditLogChangeDetails changes={log.changes} users={users} />;
        }
        if (isPassAuditLog(log) && log.audit_type === AuditType.PASS_PUNCHES_ADJUSTED) {
          return <PunchAdjustmentAuditDetails log={log} />;
        }
        if (isPassAuditLog(log) && log.changes?.length) {
          return <AuditLogChangeDetails changes={log.changes} users={users} />;
        }
        if (isOrderPageAuditLog(log) && log.changes?.length) {
          return <AuditLogChangeDetails changes={log.changes} showRawValues />;
        }
        if (isUserAuditLog(log) && log.audit_type === AuditType.USER_ROLE_CHANGED && log.old_role && log.new_role) {
          return (
            <Typography variant="body2">
              {t('auditlog.user_role_change', {
                oldRole: t(`users.roles.${log.old_role}`),
                newRole: t(`users.roles.${log.new_role}`),
              })}
            </Typography>
          );
        }
        if (log.refund) {
          return <RefundAuditDetails refund={log.refund} auditType={log.audit_type} />;
        }
        return <Typography variant="body2">-</Typography>;
      },
    },
    {
      id: 'flow',
      alignment: 'center',
      label: t('auditlog.table.columns.flow'),
      render: (log: AuditLogData) => {
        if (log.flow_chain_id) {
          return <AuditLogFlowVisualization flowChainId={log.flow_chain_id} users={users} />;
        }
        return <Typography variant="body2">-</Typography>;
      },
    },
  ];

  const { TableComponent, isLoadingData, mutate } = useTable<AuditLogData>({
    opId: `getAuditLogs-${JSON.stringify(filters)}`,
    fetcher,
    tableDetails,
    fillHeight: true,
    actionsComponent: (
      <Grid container spacing={1} alignItems="center">
        <Grid item>
          <AuditTypeFilter
            value={filters.auditTypes}
            onChange={(types) => setFilters({ ...filters, auditTypes: types })}
          />
        </Grid>
        <Grid item xs={6} sm={3} md={3}>
          <LocalizationProvider dateAdapter={AdapterMoment}>
            <DatePicker
              label={t('auditlog.filters.start_date')}
              value={filters.startDate ? moment(filters.startDate) : null}
              onChange={(date) => setFilters({ ...filters, startDate: date?.format('YYYY-MM-DD') })}
              slotProps={{ textField: { size: 'small', fullWidth: true } }}
            />
          </LocalizationProvider>
        </Grid>
        <Grid item xs={6} sm={3} md={3}>
          <LocalizationProvider dateAdapter={AdapterMoment}>
            <DatePicker
              label={t('auditlog.filters.end_date')}
              value={filters.endDate ? moment(filters.endDate) : null}
              onChange={(date) => setFilters({ ...filters, endDate: date?.format('YYYY-MM-DD') })}
              slotProps={{ textField: { size: 'small', fullWidth: true } }}
            />
          </LocalizationProvider>
        </Grid>
      </Grid>
    ),
  });

  return (
    <Grid container flexDirection="column" flexWrap="nowrap" sx={{ flex: 1, minHeight: 0 }}>
      <Grid item sx={{ px: mb.layout.cardPadding, pt: mb.layout.cardPadding, flexShrink: 0 }}>
        <Typography variant="h4">{t('auditlog.page.title')}</Typography>
      </Grid>
      {isLoadingData || isLoadingUsers ? (
        <Grid
          container
          sx={{
            minHeight: '200px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}>
          <RotatingLoader />
        </Grid>
      ) : (
        <Grid item sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
          {TableComponent}
        </Grid>
      )}
      <EditOrderDialog
        editOrderDialogState={editOrderDrawerState}
        closeEditOrderDialog={() => {
          void mutate();
          void closeEditOrderDrawer();
        }}
      />
    </Grid>
  );
};
